import { CssBaseline } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { createMuiOverrides } from './mui-overrides';
import {
  v2DarkPalette as darkPalette,
  v2LightPalette as lightPalette,
} from './palette';
import { ThemeContext } from './theme-context';
import { ThemeType } from './types';
import { typography } from './typography';

const isThemeType = (value: string | null): value is ThemeType =>
  value === ThemeType.Light || value === ThemeType.Dark;

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  // ... existing code ...
  const [themeMode, setThemeMode] = useState<ThemeType>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const passedThemeMode = searchParams.get('theme_mode');
    if (isThemeType(passedThemeMode)) {
      return passedThemeMode;
    }
    const savedTheme = localStorage.getItem('themeMode');
    return isThemeType(savedTheme) ? savedTheme : ThemeType.Dark;
  });

  useEffect(() => {
    // Save the theme preference in local storage whenever it changes
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  const colorPalette =
    themeMode === ThemeType.Light ? lightPalette : darkPalette;

  const theme = createTheme({
    components: createMuiOverrides(colorPalette, themeMode === ThemeType.Dark),
    palette: colorPalette,
    typography,
  });

  const toggleTheme = () => {
    setThemeMode((prevMode) =>
      prevMode === ThemeType.Light ? ThemeType.Dark : ThemeType.Light,
    );
  };

  return (
    <ThemeContext.Provider value={{ toggleTheme, currentTheme: themeMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
